import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { parse } from '../../util/urlHelpers';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { LISTING_TYPES } from '../../util/listingTypes';

// ================ Action Types ================ //
export const FETCH_BIDS_REQUEST = 'app/BidsBrowsePage/FETCH_BIDS_REQUEST';
export const FETCH_BIDS_SUCCESS = 'app/BidsBrowsePage/FETCH_BIDS_SUCCESS';
export const FETCH_BIDS_ERROR = 'app/BidsBrowsePage/FETCH_BIDS_ERROR';

// ================ Reducer ================ //
const initialState = {
  fetchBidsInProgress: false,
  fetchBidsError: null,
  pagination: null,
  currentPageResultIds: [],
  lastFetchTime: null,
  queryKey: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_BIDS_REQUEST:
      return {
        ...state,
        fetchBidsInProgress: true,
        fetchBidsError: null,
      };
    case FETCH_BIDS_SUCCESS:
      return {
        ...state,
        fetchBidsInProgress: false,
        pagination: payload.data.meta,
        currentPageResultIds: payload.data.data.map(l => l.id),
        lastFetchTime: Date.now(),
        queryKey: payload.queryKey,
      };
    case FETCH_BIDS_ERROR:
      return {
        ...state,
        fetchBidsInProgress: false,
        fetchBidsError: payload,
      };
    default:
      return state;
  }
}

// ================ Selectors ================ //
export const getBidsBrowsePage = state => state.BidsBrowsePage || initialState;
export const getFetchBidsError = state => getBidsBrowsePage(state).fetchBidsError;
export const isFetchingBids = state => getBidsBrowsePage(state).fetchBidsInProgress;
export const getBidsPagination = state => getBidsBrowsePage(state).pagination;
export const getBidsResultIds = state => getBidsBrowsePage(state).currentPageResultIds || [];

const BIDS_CACHE_DURATION = 30 * 1000;
const browseBidRequests = new Map();

const createQueryKey = queryParams => {
  const entries = Object.entries(queryParams || {}).filter(
    ([, value]) => value !== undefined && value !== null && value !== ''
  );
  entries.sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
};

// ================ Action Creators ================ //
export const fetchBidsRequest = () => ({
  type: FETCH_BIDS_REQUEST,
});

export const fetchBidsSuccess = (response, queryKey) => ({
  type: FETCH_BIDS_SUCCESS,
  payload: { ...response, queryKey },
});

export const fetchBidsError = error => ({
  type: FETCH_BIDS_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

/**
 * Fetch ticket request listings (Reverse Price Negotiation listings)
 * @param {Object} params - Query parameters (page, perPage, etc.)
 * @param {Object} config - Application configuration
 * @returns {Function} Redux thunk
 */
export const fetchBids = (params = {}, config = {}) => (dispatch, getState, sdk) => {
  const { page = 1, perPage = 24, ...restParams } = params;

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  // Query Sharetribe listings filtered by listingType
  const queryParams = {
    ...restParams,
    page,
    perPage,
    // Filter by listing type - this should match your Sharetribe Console configuration
    pub_listingType: LISTING_TYPES.TICKET_REQUEST,
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'description',
      'geolocation',
      'price',
      'deleted',
      'state',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.event_date',
      'publicData.eventDateTime',
      'publicData.eventDate',
      'publicData.venue',
      'publicData.event_location',
      'publicData.ticket_type',
      'publicData.number_of_tickets',
      'publicData.min_price',
      'publicData.max_price',
      'publicData.crew_id',
      'publicData.attraction_name',
      'publicData.message', // Buyer's message field
      'publicData.active',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };

  const queryKey = createQueryKey(queryParams);
  const state = getState();
  const bidsState = getBidsBrowsePage(state);
  const hasFreshCache =
    bidsState.queryKey === queryKey &&
    bidsState.lastFetchTime &&
    Date.now() - bidsState.lastFetchTime < BIDS_CACHE_DURATION &&
    Array.isArray(bidsState.currentPageResultIds) &&
    bidsState.currentPageResultIds.length > 0;

  if (hasFreshCache) {
    return Promise.resolve({
      data: {
        data: bidsState.currentPageResultIds.map(id => ({ id })),
        meta: bidsState.pagination,
      },
    });
  }

  if (browseBidRequests.has(queryKey)) {
    return browseBidRequests.get(queryKey);
  }

  dispatch(fetchBidsRequest());

  const requestPromise = sdk.listings
    .query(queryParams)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(fetchBidsSuccess(response, queryKey));
      return response;
    })
    .catch(e => {
      // Keep the route alive so the page can render an inline error message
      // instead of crashing the whole app with a thrown plain object.
      // The raw error is still useful in devtools for diagnosing the failing query.
      console.error('[BidsBrowsePage.fetchBids] listings query failed', e);
      const error = storableError(e);
      dispatch(fetchBidsError(error));
      return { error };
    })
    .finally(() => {
      browseBidRequests.delete(queryKey);
    });

  browseBidRequests.set(queryKey, requestPromise);
  return requestPromise;
};

/**
 * Load data for BidsBrowsePage
 * @param {Object} params - Route parameters
 * @param {Object} search - URL search params
 * @param {Object} config - Application configuration
 * @returns {Function} Redux thunk
 */
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const { page = 1, ...rest } = queryParams;

  return dispatch(
    fetchBids(
      {
        ...rest,
        page,
        perPage: 24,
      },
      config
    )
  );
};

// Note: Using parse from urlHelpers which handles URL search params parsing
